import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import api, { errMsg } from "../../lib/api";
import { unwrap, extractRows, extractTotal, findCounts } from "../../lib/helpers";
import { KVTable, DynamicTable } from "../../components/DataViews";
import { StatCard } from "../../components/StatCard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

const TEMPLATE_STATS = [
  { key: "approved", label: "Approved", color: "#34C759", tone: "success" },
  { key: "pending", label: "Pending approval", color: "#FFCC00", tone: "warning" },
  { key: "rejected", label: "Rejected", color: "#FF3B30", tone: "error" },
  { key: "paused", label: "Paused", color: "#0891B2", tone: "default" },
  { key: "disabled", label: "Disabled", color: "#6B7280", tone: "default" },
];
const COUNT_KEYS = ["approved", "active", "pending", "rejected", "paused", "disabled"];

export function TemplatesTab({ businessId, projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejPage, setRejPage] = useState(0);
  const [rejData, setRejData] = useState(null);
  const [rejLoading, setRejLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/projects/analytics/templates", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId },
      });
      setData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId, projectId]);

  const loadRejections = useCallback(async () => {
    setRejLoading(true);
    try {
      const res = await api.get("/dashboard/projects/analytics/templates/rejections", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId, page: rejPage, size: 25 },
      });
      setRejData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRejLoading(false);
    }
  }, [businessId, projectId, rejPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRejections(); }, [loadRejections]);

  const counts = useMemo(() => {
    const c = findCounts(data, COUNT_KEYS);
    if (c && c.approved === undefined && typeof c.active === "number") c.approved = c.active;
    return c;
  }, [data]);

  const rejRows = extractRows(rejData);
  const rejTotal = extractTotal(rejData, rejRows.length);
  const pieData = TEMPLATE_STATS.filter((s) => counts?.[s.key] > 0).map((s) => ({
    name: s.label, value: counts[s.key], color: s.color,
  }));

  return (
    <div className="space-y-6" data-testid="templates-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => { load(); loadRejections(); }} data-testid="templates-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {TEMPLATE_STATS.map((s) => (
              <StatCard
                key={s.key}
                label={s.label}
                value={counts?.[s.key]?.toLocaleString() ?? "—"}
                tone={s.tone}
                testId={`tpl-stat-${s.key}`}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pieData.length > 0 && (
              <div className="border border-border rounded-md bg-card p-5" data-testid="tpl-pie-chart">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Approval distribution</div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} strokeWidth={2}>
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <details className="group self-start w-full">
              <summary className="text-xs uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="tpl-raw-toggle">
                Raw template payload
              </summary>
              <div className="mt-3">
                <KVTable data={data} testId="tpl-raw-table" />
              </div>
            </details>
          </div>
        </>
      )}

      <section className="space-y-3">
        <h3 className="text-base font-display font-bold tracking-tight">Rejected templates</h3>
        {rejLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <DynamicTable rows={rejRows} testId="rejections-table" empty="No rejected templates." />
        )}
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground font-data" data-testid="rejections-page-info">
            {rejTotal} record{rejTotal === 1 ? "" : "s"} · page {rejPage + 1}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={rejPage === 0 || rejLoading} onClick={() => setRejPage((p) => p - 1)} data-testid="rejections-prev">
              <CaretLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={rejLoading || rejRows.length < 25} onClick={() => setRejPage((p) => p + 1)} data-testid="rejections-next">
              Next <CaretRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
